// Knowledge Graph Assessment Bridge Service
// Bridges the Knowledge Graph module with the Assessment module for seamless data flow

import { allSamples } from '../data/knowledgeGraph';
import hkdseQuestionLoader from './hkdseQuestionLoader';

class KnowledgeGraphAssessmentBridge {
  constructor() {
    this.knowledgeGraphData = allSamples;
    this.questionLoader = hkdseQuestionLoader;
  }

  // Get all available HKDSE years from Knowledge Graph
  getAvailableHKDSEYears() {
    const hkdseData = this.knowledgeGraphData.hkdse;
    const years = [];
    
    Object.keys(hkdseData).forEach(key => {
      if (key.startsWith('hkdse')) {
        const year = key.replace('hkdse', '');
        if (year && !isNaN(year)) {
          years.push(parseInt(year));
        }
      }
    });
    
    return years.sort((a, b) => b - a); // Sort in descending order (newest first)
  }

  // Get HKDSE problems for a specific year from Knowledge Graph
  getHKDSEProblemsByYear(year) {
    const yearKey = `hkdse${year}`;
    const yearData = this.knowledgeGraphData.hkdse[yearKey];
    
    if (!yearData) {
      return [];
    }

    // First, collect all problems for the year without assigning numbers
    let allProblemsForYear = [];
    Object.keys(yearData).forEach(category => {
      const categoryData = yearData[category];
      categoryData.forEach(problem => {
        allProblemsForYear.push({
          id: problem.id,
          year: year,
          problem: problem.problem,
          problemType: problem.problemType,
          difficulty: problem.difficulty,
          description: problem.description,
          graphData: problem.graphData,
          marks: this.estimateMarks(problem.difficulty),
          content: `${problem.problem} (${year} - ${problem.problemType})`,
          figureDescription: this.extractFigureDescription(problem),
          parts: this.generatePartsFromGraphData(problem.graphData)
        });
      });
    });

    // Now assign global sequential numbers and update displayText
    let globalQuestionNumber = 1;
    allProblemsForYear = allProblemsForYear.map(problem => ({
      ...problem,
      number: globalQuestionNumber++,
      displayText: `${year} Q.${problem.number}`
    }));

    return allProblemsForYear;
  }

  // Get all HKDSE problems from Knowledge Graph
  getAllHKDSEProblems() {
    const years = this.getAvailableHKDSEYears();
    const allProblems = [];
    
    years.forEach(year => {
      const yearProblems = this.getHKDSEProblemsByYear(year);
      allProblems.push(...yearProblems);
    });
    
    return allProblems;
  }

  // Search HKDSE problems by text
  searchHKDSEProblems(searchText) {
    const allProblems = this.getAllHKDSEProblems();
    const searchLower = searchText.toLowerCase();
    
    return allProblems.filter(problem => 
      problem.problem.toLowerCase().includes(searchLower) ||
      problem.description.toLowerCase().includes(searchLower) ||
      problem.problemType.toLowerCase().includes(searchLower) ||
      problem.content.toLowerCase().includes(searchLower)
    );
  }

  // Get problem by ID
  getProblemById(problemId) {
    const allProblems = this.getAllHKDSEProblems();
    return allProblems.find(problem => problem.id === problemId);
  }

  // Convert Knowledge Graph problem to Assessment format
  convertToAssessmentFormat(knowledgeGraphProblem) {
    return {
      id: knowledgeGraphProblem.id,
      year: knowledgeGraphProblem.year,
      number: knowledgeGraphProblem.number,
      problem: knowledgeGraphProblem.problem,
      problemType: knowledgeGraphProblem.problemType,
      difficulty: knowledgeGraphProblem.difficulty,
      description: knowledgeGraphProblem.description,
      graphData: knowledgeGraphProblem.graphData,
      marks: knowledgeGraphProblem.marks,
      content: knowledgeGraphProblem.content,
      figureDescription: knowledgeGraphProblem.figureDescription,
      parts: knowledgeGraphProblem.parts,
      displayText: `${knowledgeGraphProblem.year} Q.${knowledgeGraphProblem.number}`
    };
  }

  // Extract question number from problem ID
  extractQuestionNumber(problemId) {
    const match = problemId.match(/_(\d+)$/);
    return match ? parseInt(match[1]) : 1;
  }

  // Estimate marks based on difficulty
  estimateMarks(difficulty) {
    const markEstimates = {
      'fundamental': 2,
      'basic': 3,
      'intermediate': 4,
      'advanced': 5
    };
    return markEstimates[difficulty] || 3;
  }

  // Extract figure description from problem
  extractFigureDescription(problem) {
    const problemText = problem.problem.toLowerCase();
    let description = '';

    console.log('Extracting figure description from:', problemText);

    // Check for quadrilateral with extensions (like the △ACE and △DBE problem)
    if (problemText.includes('quadrilateral') && problemText.includes('extension')) {
      description = 'Quadrilateral ABCD with extensions of AB and CD intersecting at point E. ';
      
      // Check for specific triangle names
      if (problemText.includes('ace') && problemText.includes('dbe')) {
        description += 'Triangles △ACE and △DBE with extensions meeting at E. ';
      }
      
      // Check for similarity
      if (problemText.includes('similar') || problemText.includes('sim')) {
        description += 'The triangles are similar. ';
      }
      
      // Check for specific measurements
      const measurementMatches = problemText.match(/([a-z]{2})\s*=\s*(\d+)\s*cm/gi);
      if (measurementMatches) {
        description += 'Given measurements: ' + measurementMatches.join(', ') + '. ';
      }
      
      console.log('Generated quadrilateral description:', description);
      return description;
    }

    // Enhanced perpendicular lines detection with coordinates
    if (problemText.includes('perpendicular') || problemText.includes('l₁') || problemText.includes('l₂') || problemText.includes('l1') || problemText.includes('l2')) {
      description = 'Two perpendicular lines ';
      
      // Check for specific line names
      if (problemText.includes('l₁') || problemText.includes('l1')) {
        description += 'L₁ ';
      }
      if (problemText.includes('l₂') || problemText.includes('l2')) {
        description += 'and L₂ ';
      }
      
      // Enhanced coordinate extraction
      const coordinateMatches = problemText.match(/\(([^)]+)\)/g);
      if (coordinateMatches) {
        description += 'with coordinates: ' + coordinateMatches.join(', ') + '. ';
      }
      
      // Check for intersection point with coordinates
      const intersectionMatch = problemText.match(/intersect.*?point\s*\(([^)]+)\)/);
      if (intersectionMatch) {
        description += `Intersecting at point ${intersectionMatch[1]} `;
      }
      
      // Check for y-intercept
      const yInterceptMatch = problemText.match(/y.?intercept.*?(\d+)/);
      if (yInterceptMatch) {
        description += `with y-intercept at ${yInterceptMatch[1]} `;
      }
      
      // Check for region enclosed
      if (problemText.includes('region') && problemText.includes('enclosed')) {
        description += 'forming a region R enclosed by the lines and x-axis';
      }
      
      const finalDescription = description.trim();
      console.log('Generated perpendicular lines description:', finalDescription);
      return finalDescription;
    }

    // Enhanced circle detection with detailed properties
    if (problemText.includes('circle') || problemText.includes('圓')) {
      description = 'Circle ';
      
      // Check for diameter
      if (problemText.includes('diameter') || problemText.includes('直徑')) {
        description += 'with diameter ';
        const diameterMatch = problemText.match(/([a-z]{2})\s*為.*?直徑/);
        if (diameterMatch) {
          description += `${diameterMatch[1]} `;
        }
      }
      
      // Check for chord
      if (problemText.includes('chord') || problemText.includes('弦')) {
        description += 'and chord ';
        const chordMatch = problemText.match(/([a-z]{2})\s*為.*?弦/);
        if (chordMatch) {
          description += `${chordMatch[1]} `;
        }
      }
      
      // Check for intersection point
      const circleIntersectionMatch = problemText.match(/交點記為\s*([a-z])/);
      if (circleIntersectionMatch) {
        description += `intersecting at point ${circleIntersectionMatch[1]} `;
      }
      
      // Check for specific angles
      const angleMatches = problemText.match(/∠([a-z]{3})\s*=\s*(\d+)°/g);
      if (angleMatches) {
        description += 'with angles: ' + angleMatches.join(', ') + '. ';
      }
      
      // Check for radius
      const radiusMatch = problemText.match(/radius\s*=\s*(\d+)/);
      if (radiusMatch) {
        description += `with radius ${radiusMatch[1]}. `;
      }
      
      console.log('Generated circle description:', description);
      return description;
    }

    // Enhanced parallel lines detection
    if (problemText.includes('parallel') || problemText.includes('平行')) {
      description = 'Two parallel lines ';
      
      // Check for specific line names
      const parallelMatch = problemText.match(/([a-z]{2})\s*∥\s*([a-z]{2})/);
      if (parallelMatch) {
        description += `${parallelMatch[1]} parallel to ${parallelMatch[2]} `;
      }
      
      // Check for intersection with other lines
      const parallelIntersectionMatch = problemText.match(/([a-z]{2})\s*與\s*([a-z]{2})\s*相交/);
      if (parallelIntersectionMatch) {
        description += `intersecting with ${parallelIntersectionMatch[1]} and ${parallelIntersectionMatch[2]} `;
      }
      
      console.log('Generated parallel lines description:', description);
      return description;
    }

    // Enhanced triangle detection with detailed properties
    if (problemText.includes('triangle') || problemText.includes('abc') || problemText.includes('△')) {
      description = 'Triangle ';
      
      // Check for specific triangle names
      const triangleMatch = problemText.match(/△([a-z]{3})/);
      if (triangleMatch) {
        description += `△${triangleMatch[1]} `;
      }
      
      // Check for similarity
      if (problemText.includes('similar') || problemText.includes('sim') || problemText.includes('∼')) {
        description += 'with similar triangles ';
        const similarMatch = problemText.match(/△([a-z]{3})\s*∼\s*△([a-z]{3})/);
        if (similarMatch) {
          description += `△${similarMatch[1]} similar to △${similarMatch[2]} `;
        }
      }
      
      // Check for right angle
      if (problemText.includes('right') || problemText.includes('直角')) {
        description += 'with right angle ';
      }
      
      // Check for specific measurements
      const triangleMeasurementMatches = problemText.match(/([a-z]{2})\s*=\s*(\d+)\s*cm/gi);
      if (triangleMeasurementMatches) {
        description += 'Given measurements: ' + triangleMeasurementMatches.join(', ') + '. ';
      }
      
      // Check for angle measurements
      const triangleAngleMatches = problemText.match(/∠([a-z]{3})\s*=\s*(\d+)°/g);
      if (triangleAngleMatches) {
        description += 'with angles: ' + triangleAngleMatches.join(', ') + '. ';
      }
      
      console.log('Generated triangle description:', description);
      return description;
    }

    // Enhanced coordinate geometry detection
    if (problemText.includes('coordinate') || (problemText.includes('point') && problemText.includes('('))) {
      description = 'Coordinate geometry with ';
      
      // Check for specific points with coordinates
      const coordinateMatches = problemText.match(/\(([^)]+)\)/g);
      if (coordinateMatches) {
        description += 'points at coordinates: ' + coordinateMatches.join(', ') + '. ';
      }
      
      // Check for lines between points
      const lineMatches = problemText.match(/([a-z]{2})\s*與\s*([a-z]{2})/g);
      if (lineMatches) {
        description += 'Lines connecting: ' + lineMatches.join(', ') + '. ';
      }
      
      // Check for specific point names with coordinates
      const pointCoordinateMatches = problemText.match(/([a-z])\s*\(([^)]+)\)/g);
      if (pointCoordinateMatches) {
        description += 'Named points: ' + pointCoordinateMatches.join(', ') + '. ';
      }
      
      console.log('Generated coordinate geometry description:', description);
      return description;
    }

    // Enhanced polygon detection
    const geometricTerms = ['square', 'rectangle', 'parallelogram', 'trapezium', 'quadrilateral', 'pentagon', 'hexagon'];
    for (const term of geometricTerms) {
      if (problemText.includes(term)) {
        description = `${term.charAt(0).toUpperCase() + term.slice(1)} `;
        
        // Check for specific properties
        if (problemText.includes('diagonal')) {
          description += 'with diagonals ';
        }
        
        if (problemText.includes('area')) {
          description += 'with specified area ';
        }
        
        // Check for side measurements
        const sideMatches = problemText.match(/([a-z]{2})\s*=\s*(\d+)\s*cm/gi);
        if (sideMatches) {
          description += 'with sides: ' + sideMatches.join(', ') + '. ';
        }
        
        console.log('Generated geometric shape description:', description);
        return description;
      }
    }

    // Enhanced general geometric content detection
    if (problemText.includes('figure') || problemText.includes('diagram') || problemText.includes('圖')) {
      description = 'Geometric figure with specified properties';
      
      // Extract any measurements mentioned
      const measurementMatches = problemText.match(/(\d+)\s*(cm|units|degrees)/g);
      if (measurementMatches) {
        description += ' including measurements: ' + measurementMatches.join(', ') + '. ';
      }
      
      console.log('Generated general geometric description:', description);
      return description;
    }

    // Enhanced fallback: Provide more context from the question
    console.log('No specific figure description found, using enhanced question analysis');
    
    // Extract key geometric terms from the question
    const geometricKeywords = [];
    const keywords = ['line', 'point', 'angle', 'triangle', 'circle', 'square', 'rectangle', 'parallel', 'perpendicular', 'intersect', 'extension', 'coordinate', 'measurement', 'similar', 'right', 'acute', 'obtuse'];
    
    keywords.forEach(keyword => {
      if (problemText.includes(keyword)) {
        geometricKeywords.push(keyword);
      }
    });
    
    if (geometricKeywords.length > 0) {
      description = `Geometric figure involving: ${geometricKeywords.join(', ')}. `;
    }
    
    // Extract any coordinates mentioned
    const coordinateMatches = problemText.match(/\(([^)]+)\)/g);
    if (coordinateMatches) {
      description += `Coordinates mentioned: ${coordinateMatches.join(', ')}. `;
    }
    
    // Extract any measurements mentioned
    const measurementMatches = problemText.match(/(\d+)\s*(cm|units|degrees)/g);
    if (measurementMatches) {
      description += `Measurements: ${measurementMatches.join(', ')}. `;
    }
    
    // Add the full question text as context
    description += problem.problem;
    
    console.log('Generated enhanced fallback description:', description);
    return description;
  }

  // Generate parts from graph data
  generatePartsFromGraphData(graphData) {
    if (!graphData || !graphData.nodes) {
      return [];
    }

    const steps = graphData.nodes.filter(node => node.type === 'steps');
    const concepts = graphData.nodes.filter(node => node.type === 'concepts');
    
    const parts = [];
    
    // Create parts from steps
    steps.forEach((step, index) => {
      parts.push({
        id: String.fromCharCode(97 + index), // a, b, c, etc.
        question: step.description,
        marks: 2
      });
    });
    
    // If no steps, create parts from concepts
    if (parts.length === 0 && concepts.length > 0) {
      concepts.slice(0, 3).forEach((concept, index) => {
        parts.push({
          id: String.fromCharCode(97 + index),
          question: concept.description,
          marks: 2
        });
      });
    }
    
    return parts;
  }

  // Get dropdown data for Assessment component
  getDropdownData() {
    const years = this.getAvailableHKDSEYears();
    const dropdownData = [];
    
    years.forEach(year => {
      const yearProblems = this.getHKDSEProblemsByYear(year);
      yearProblems.forEach(problem => {
        dropdownData.push(this.convertToAssessmentFormat(problem));
      });
    });
    
    return dropdownData;
  }

  // Get problems grouped by year for dropdown
  getProblemsGroupedByYear() {
    const years = this.getAvailableHKDSEYears();
    const grouped = {};
    
    years.forEach(year => {
      grouped[year] = this.getHKDSEProblemsByYear(year).map(problem => 
        this.convertToAssessmentFormat(problem)
      );
    });
    
    return grouped;
  }

  // Get question content for input box (raw LaTeX format for processing)
  getQuestionContent(problem) {
    let content = `Question ${problem.number} (${problem.marks} marks)\n`;
    content += problem.problem + '\n\n';
    
    // Don't include figure description in the input textarea
    // as it will be handled separately in the rendered preview
    
    if (problem.parts && problem.parts.length > 0) {
      problem.parts.forEach(part => {
        content += `(${part.id}) ${part.question}\n`;
        
        if (part.subParts) {
          part.subParts.forEach(subPart => {
            content += `(${subPart.id}) ${subPart.question}\n`;
          });
        }
      });
    }
    
    return content;
  }

  // Get LaTeX content for rendering (with proper MathJax formatting)
  getLatexContent(problem) {
    return {
      question: `Question ${problem.number} (${problem.marks} marks)`,
      problem: problem.problem,
      figureDescription: problem.figureDescription,
      parts: problem.parts || []
    };
  }
}

export default new KnowledgeGraphAssessmentBridge(); 